import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PINEntry = ({ staffInfo, onPINSubmit, onBack, loading, error }) => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs?.current?.[0]?.focus();
  }, []);

  const handlePinChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/?.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (index === 5 && value && newPin?.every(digit => digit)) {
      handleSubmit(newPin?.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e?.key === 'Backspace' && !pin?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 6);
    
    if (/^\d{6}$/?.test(pastedData)) {
      const newPin = pastedData?.split('');
      setPin(newPin);
      inputRefs?.current?.[5]?.focus();
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = (pinValue = null) => {
    const finalPin = pinValue || pin?.join('');
    if (finalPin?.length === 6) {
      onPINSubmit?.(finalPin);
    }
  };

  const handleClear = () => {
    setPin(['', '', '', '', '', '']);
    inputRefs?.current?.[0]?.focus();
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        {/* Staff Info */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center border-2 border-success">
            <Icon name="CheckCircle" size={40} className="text-success" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">QR Code Verified</h2>
        <p className="text-muted-foreground mb-1">
          Welcome, <span className="font-semibold text-foreground">{staffInfo?.full_name}</span>
        </p>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
          {staffInfo?.role}
        </span>
      </div>

      {/* PIN Entry */}
      <div className="max-w-md mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-foreground">Enter Your 6-Digit PIN</label>
            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
            >
              <Icon name={showPin ? 'EyeOff' : 'Eye'} size={16} />
              <span>{showPin ? 'Hide' : 'Show'}</span>
            </button>
          </div>

          <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {pin?.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e?.target?.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={loading}
                className="w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-error/10 rounded-lg border border-error/20 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => handleSubmit()}
            disabled={loading || pin?.some(digit => !digit)}
            size="lg"
            className="w-full"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={20} className="animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <Icon name="Lock" size={20} />
                <span>Verify PIN & Clock In</span>
              </>
            )}
          </Button>

          <div className="flex gap-3">
            <Button
              onClick={handleClear}
              variant="outline"
              size="lg"
              className="flex-1"
              disabled={loading}
            >
              <Icon name="RotateCcw" size={20} />
              <span>Clear</span>
            </Button>

            <Button
              onClick={onBack}
              variant="outline"
              size="lg"
              className="flex-1"
              disabled={loading}
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Back</span>
            </Button>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={18} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Security Notice</p>
              <p>After 5 failed PIN attempts, your account will be locked for 15 minutes.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PINEntry;